import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, User, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import {
  AdminAccessError,
  AdminAction,
  AdminModuleId,
  MyAdminAccess,
  adminAccessService,
} from '../services/firebaseFunctions';

/**
 * Controle de Acesso Administrativo (RBAC) — REEDY-17.
 *
 * Substitui a checagem binária por UID único (`REACT_APP_ADMIN_UID`) pelo gate de dois fatores
 * do contrato (`.agents/memory/contracts.md` §"Controle de Acesso Administrativo (RBAC)"):
 * a claim `admin` (fator 1) só é significativa junto do documento `admin_users/{uid}` ativo
 * (fator 2) — e é a callable `getMyAdminAccess` que resolve os dois fatores no backend. O
 * frontend nunca lê `admin_users` diretamente (coleção fechada ao SDK cliente).
 *
 * `adminAccessStatus` distingue explicitamente três desfechos que NÃO podem parecer a mesma
 * coisa na UI (jornada J3 / risco S-14):
 *  - 'denied'      → `permission-denied`: não-admin OU admin desativado (a mesma resposta para os
 *                    dois casos, deliberado pelo contrato, para não virar oráculo de existência
 *                    de conta administrativa).
 *  - 'unavailable' → qualquer outro erro (rede, timeout, erro interno) — nunca deve ser tratado
 *                    como "sem permissão", nem gerar loop de redirecionamento ou tela em branco.
 *  - 'ready'       → dois fatores satisfeitos; `adminAccess.permissions` é a matriz vigente.
 */
export type AdminAccessStatus = 'loading' | 'ready' | 'denied' | 'unavailable';

const ACTION_RANK: Record<AdminAction, number> = { ver: 1, editar: 2 };

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  firebaseUser: User | null;
  user: string | null;
  adminAccess: MyAdminAccess | null;
  adminAccessStatus: AdminAccessStatus;
  /** `'ver'` é satisfeita por `'ver'` OU `'editar'`; `'editar'` só por `'editar'`. */
  hasPermission: (moduleId: AdminModuleId, action: AdminAction) => boolean;
  /** Predicado derivado (`contracts.md`): gestor = ativo + `administradores: 'editar'`. */
  isAdminManager: boolean;
  /**
   * Reconsulta `getMyAdminAccess` sob demanda (jornada J5 — concessão/revogação observadas em
   * sessão já aberta). Não há listener ao vivo de Firestore para permissão (a coleção é fechada
   * ao cliente), então refletir uma mudança na sessão de quem já está logado depende deste
   * disparo explícito — exposto para uso em ações da UI (ex.: "Atualizar permissões").
   */
  refreshAdminAccess: () => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [adminAccess, setAdminAccess] = useState<MyAdminAccess | null>(null);
  const [adminAccessStatus, setAdminAccessStatus] = useState<AdminAccessStatus>('loading');

  const loadAdminAccess = useCallback(async (user: User) => {
    setAdminAccessStatus('loading');
    try {
      const access = await adminAccessService.getMyAdminAccess();
      setAdminAccess(access);
      setAdminAccessStatus('ready');
      // Dupla checagem (contracts.md): as Firestore Rules leem a claim DO TOKEN (~1h de
      // obsolescência); o backend a lê ao vivo. Forçar a renovação aqui antecipa a propagação
      // — nunca concede nada por si só, apenas evita que uma leitura direta (ex.: delivery_queue)
      // seja negada por alguns minutos após uma concessão que já é válida no backend.
      try {
        await user.getIdToken(true);
      } catch {
        // Falha ao renovar o token não derruba a sessão — é mitigação de atrito, não de segurança.
      }
    } catch (caught) {
      const error = caught as AdminAccessError;
      setAdminAccess(null);
      // 'permission-denied' é a resposta deliberada tanto para "não é administrador" quanto para
      // "administrador desativado" (contrato: não vira oráculo). Qualquer outro código é falha de
      // transporte/backend — nunca deve ser confundido com negação de permissão.
      setAdminAccessStatus(error?.code === 'permission-denied' ? 'denied' : 'unavailable');
    }
  }, []);

  const refreshAdminAccess = useCallback(async () => {
    if (!auth.currentUser) return;
    await loadAdminAccess(auth.currentUser);
  }, [loadAdminAccess]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      if (nextUser) {
        setFirebaseUser(nextUser);
        setIsAuthenticated(true);
        void loadAdminAccess(nextUser);
      } else {
        setFirebaseUser(null);
        setIsAuthenticated(false);
        setAdminAccess(null);
        setAdminAccessStatus('loading');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [loadAdminAccess]);

  const hasPermission = useCallback(
    (moduleId: AdminModuleId, action: AdminAction): boolean => {
      if (adminAccessStatus !== 'ready' || !adminAccess) return false;
      const granted = adminAccess.permissions[moduleId];
      if (!granted) return false;
      return ACTION_RANK[granted] >= ACTION_RANK[action];
    },
    [adminAccess, adminAccessStatus]
  );

  const isAdminManager = adminAccessStatus === 'ready'
    && adminAccess?.status === 'active'
    && adminAccess?.permissions.administradores === 'editar';

  const loginWithEmail = async (email: string, password: string): Promise<boolean> => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return true;
    } catch (error) {
      return false;
    }
  };

  const logout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      // Silenciar erro de logout
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    loading,
    firebaseUser,
    user: firebaseUser?.email ?? null,
    adminAccess,
    adminAccessStatus,
    hasPermission,
    isAdminManager,
    refreshAdminAccess,
    loginWithEmail,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
